import { readPayload } from '@c360/shared';
import { RevisionState } from '@c360/types';

// Moved to @c360/shared so the business-api MCP server describes events the same way.
export { readPayload };

/**
 * Normalize an SES revision state to the numeric RevisionState enum. The SSE
 * bridge sends the proto enum by name ('UPDATE'), while the rest of the UI
 * compares against the numeric members — without this every live UPDATE and
 * DELETE was silently ignored until a refresh. A missing state (proto3 drops
 * the zero value) is UNKNOWN_REVISION_STATE, never DELETE.
 */
export function normalizeRevisionState(state: unknown): number {
  if (typeof state === 'number') return state;
  if (typeof state === 'string' && state in RevisionState) {
    return RevisionState[state as keyof typeof RevisionState];
  }
  return RevisionState.UNKNOWN_REVISION_STATE;
}

/**
 * Build the shared base of a normalized SportEvent — all the sport-agnostic
 * fields (id, time, clock, revision, sourceNativeId, timestamp, ...). The
 * returned object's `payload` is left undefined for the per-sport parser
 * to set.
 *
 * Return type is `any` because the @c360/types `Event.payload` is a strict
 * union of NHL event-type strings, which fights our runtime `{type, data}`
 * shape. The cast is contained here so per-sport parsers stay tidy.
 */
export function makeBaseSportEvent(rawEvent: any): any {
  return {
    ...rawEvent,
    payload: undefined,
    id: rawEvent?.id,
    time: rawEvent?.time,
    description: rawEvent?.description,
    clock: rawEvent?.clock,
    sourceNativeId: rawEvent?.source_native_id || rawEvent?.sourceNativeId,
    resumeToken: rawEvent?.resume_token || rawEvent?.resumeToken,
    revision: {
      state: normalizeRevisionState(rawEvent?.revision?.state),
      version: rawEvent?.revision?.version,
    },
    timestamp: rawEvent?.time != null ? new Date(rawEvent.time) : undefined,
    timestampMs: rawEvent?.time,
  };
}

/**
 * Extract a participant's stable player id (`id.main`) as a string. Every
 * sport's wire format exposes player ids under `id.main`, and that's the same
 * id shifts are keyed on — so this is the join key between events and players.
 */
export function participantMainId(participant: any): string | null {
  const main = participant?.id?.main;
  return main == null ? null : String(main);
}

/**
 * Building block for `StatDefinition.attribute`: credit the player named in
 * `data[field]` when the event's type is `types` (one type or a list). Returns
 * `[]` when the type doesn't match or the participant is absent (e.g. team
 * rebounds carry no `player`). Each credit is a plain event count (value 1);
 * weighted stats such as NBA points build their own attributor.
 */
export function creditField(
  event: any,
  types: string | ReadonlyArray<string>,
  field: string,
): Array<{ playerId: string }> {
  const { type, data } = readPayload(event);
  const matches = typeof types === 'string' ? type === types : types.includes(type as string);
  if (!matches || !data) return [];
  const playerId = participantMainId(data[field]);
  return playerId ? [{ playerId }] : [];
}
